import {
  Component,
  OnInit,
  ViewChild,
  OnDestroy,
  ChangeDetectionStrategy,
  ElementRef,
} from '@angular/core';
import { ProductListService } from '../../../product-list-page/services/product-list.service';
import {
  Observable,
  Subject,
  distinctUntilChanged,
  filter,
  of,
  pairwise,
  takeUntil,
  tap,
} from 'rxjs';
import { GlobalService } from '../../../../SGRE-shared/services/global.service';
import {
  AutoComplete,
  AutoCompleteCompleteEvent,
  AutoCompleteSelectEvent,
} from 'primeng/autocomplete';
import { NavigationEnd, Router } from '@angular/router';
import { AppConstants } from '../../../../SGRE-shared/constants/app-constant';
import { FormBuilder, FormGroup } from '@angular/forms';
import {
  SearchBoxComponentService,
  SearchBoxConfig,
} from '@spartacus/storefront';
import { SearchBoxCategory } from '../../../product-list-page/models/plpInfo';
import { StorageService } from '../../../../SGRE-shared/services/storage.service';
import { LegalEntity } from '../../../../SGRE-shared/models/legalEntityList.model';
import { SearchBoxHeaderTrackingService } from '../../services/search-box-header-tracking.service';
import { select, Store } from '@ngrx/store';
import { selectDefaultLegalEntity } from '../../../../SGRE-shared/store/shared-store/selectors/shared.selector';

@Component({
  selector: 'app-search-box-header',
  templateUrl: './search-box-header.component.html',
  styleUrl: './search-box-header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SearchBoxHeaderComponent implements OnInit, OnDestroy {

  public userLoggedIn: Observable<LegalEntity>;
  public displayCarts: boolean;
  public searchForm: FormGroup;
  public searchedQuery: string;
  private unsubscribe$ = new Subject<void>();
  allowToSearch = true;
  retainQueryFlag: boolean;

  @ViewChild('autocomplete', { static: false }) autocomplete: AutoComplete;
  @ViewChild('searchButton', { static: false }) searchButton!: ElementRef;
  @ViewChild('searchInput', { static: false }) searchInputElement!: ElementRef;

  private searchConfig: SearchBoxConfig = {
    displayProducts: true,
    displayProductImages: true,
    maxProducts: 5,
    minCharactersBeforeRequest: 1,
  };
  searchSuggestion$: Observable<SearchBoxConfig[]>;
  previousSearchQuery = '';

  get searchInput() {
    return this.searchForm.get('searchInput');
  }

  constructor(
    private fb: FormBuilder,
    private productsService: ProductListService,
    private globalService: GlobalService,
    private router: Router,
    private searchBoxComponentService: SearchBoxComponentService,
    private storageService: StorageService,
    private searchBoxHeaderTrackingService: SearchBoxHeaderTrackingService,
    private store: Store
  ) {
    this.searchForm = this.fb.group({
      searchInput: [''],
    });
  }

  ngOnInit(): void {
    /** -- OOTB Implementation -- */
    this.isAllowedToSearch();
    this.clearSearchOnRouteChange();
    this.clearSearchOnLegalEntityChange();
    this.getSearchResults();
    this.clearSearchOnFilterClear();

    /** -- Previous Implementation -- */
    this.userLoggedIn = this.globalService.defaultLegalEntitySubject$.pipe(
      tap(() => (this.displayCarts = this.checkAccess()))
    );
  }

  isAllowedToSearch() {
    this.store.pipe(
      select(selectDefaultLegalEntity),
      tap((defaultLegalEntity) => {
        const userId = this.globalService.getUserId();
        const legalEntityName = defaultLegalEntity?.name ?? this.storageService.defaultLegalEntity?.name ?? null;

        // Determine if the user is allowed to search
        this.allowToSearch = !!(legalEntityName || userId === 'anonymous');
      }),
      takeUntil(this.unsubscribe$)
    ).subscribe();
  }

  clearSearchOnRouteChange(): void {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        distinctUntilChanged(),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((event: NavigationEnd) => {
        if (!this.retainQueryFlag) {
          this.searchForm.reset();
          this.previousSearchQuery = null;
        } else {
          this.retainQueryFlag = false;
        }
      });
  }

  clearSearchOnLegalEntityChange(): void {
    this.store.pipe(
      select(selectDefaultLegalEntity),
      pairwise(),
      filter(([previousLegalEntity, currentLegalEntity]) =>
        !!previousLegalEntity?.uid &&
        !!currentLegalEntity?.uid &&
        previousLegalEntity.uid !== currentLegalEntity.uid
      ),
      tap(() => {
        this.searchForm.reset();
        this.searchedQuery = '';
        this.previousSearchQuery = null;
        this.storageService.query = '';
      }),
      takeUntil(this.unsubscribe$)
    ).subscribe();
  }

  clearSearchOnFilterClear(): void {
    this.productsService.clearFilter$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.searchForm.reset();
        this.searchedQuery = '';
        this.previousSearchQuery = null;
      });
  }

  checkAccess(): boolean {
    return (this.storageService.userRoles || []).includes('Requester');
  }

  onFilter(event: AutoCompleteCompleteEvent): void {
    const query = event.query;
    this.searchedQuery = query; // required for the 'handleFocus()' method
    if (this.allowToSearch) {
      this.search(query);
    } else {
      // When user neither has any associated LE, nor he is anonymous
      this.searchSuggestion$ = of([]);
    }
  }

  search(queryVal: string, configVal: SearchBoxConfig = this.searchConfig): void {
    this.searchBoxComponentService.search(queryVal, configVal);
  }

  getSearchResults(): void {
    this.searchSuggestion$ = this.productsService.getSearchBoxResults(
      this.searchConfig
    );
  }

  navigateToPlp(queryText = this.searchInput.value): void {
    this.storageService.query = queryText;
    this.retainQueryFlag = true;
    this.productsService._searchConfig = this.productsService.DEFAULT_SEARCH_CONFIG;

    // Track main search analytics
    this.searchBoxHeaderTrackingService.trackSearchAnalytics(
      queryText,
      queryText,
      this.searchButton?.nativeElement,
      'Product Search'
    );

    this.router.navigate([AppConstants.routeUrls.plp]);
  }

  onSelect(event: AutoCompleteSelectEvent): void {
    const selectedResult = event?.value;
    const category = selectedResult?.category;
    const originalSearchTerm = this.searchInput.value;
    const clickTarget = event.originalEvent?.target as HTMLElement;

    this.searchInput.reset();

    if (category === SearchBoxCategory.PRODUCTS || category === SearchBoxCategory.PART_NUMBERS) {
      const selectedText = selectedResult?.name || selectedResult?.code || '';

      // Track main search analytics
      this.searchBoxHeaderTrackingService.trackSearchAnalytics(
        originalSearchTerm,
        selectedText,
        clickTarget,
        category === SearchBoxCategory.PRODUCTS ? 'Product Search' : 'Part Number Search'
      );

      this.router.navigate([this.globalService.getProductUrl(selectedResult.url)]);
    } else if (category === SearchBoxCategory.CATEGORIES) {
      const selectedQuery = selectedResult?.query || '';

      // Track main search analytics
      this.searchBoxHeaderTrackingService.trackSearchAnalytics(
        originalSearchTerm,
        selectedQuery,
        clickTarget,
        'Category Search'
      );

      this.updateProductsList(selectedQuery, true, selectedResult.selected);
    }
  }

  updateProductsList(
    queryText = this.searchInput.value,
    click = false,
    isSelected = false
  ): void {
    if (!this.router.url.includes(AppConstants.routeUrls.plp)) {
      if (click) {
        queryText = this.productsService.getModifiedQuery(queryText, isSelected ?? false);
      }
      this.navigateToPlp(queryText);
    } else if (
      (queryText?.length >= 3 || queryText?.length === 0) &&
      this.previousSearchQuery !== queryText
    ) {
      this.previousSearchQuery = queryText;
      let paramVal = queryText?.length > 0 ? queryText : ' ';

      if (queryText?.length > 0) {
        // Track search on PLP page
        this.searchBoxHeaderTrackingService.trackSearchAnalytics(
          queryText,
          queryText,
          this.searchInputElement?.nativeElement,
          'Product Search - PLP'
        );
      }

      this.productsService.search(paramVal, this.productsService.DEFAULT_SEARCH_CONFIG);
    }
  }

  handleFocus(): void {
    const currentValue = this.searchInput.value;
    if (currentValue === this.searchedQuery) {
      this.autocomplete.show();
    }
  }

  handleEnter(): void {
    const searchTerm = this.searchInput.value;
    if (!this.router.url.includes(AppConstants.routeUrls.plp)) {
      // Track main search analytics for Enter key
      this.searchBoxHeaderTrackingService.trackSearchAnalytics(
        searchTerm,
        searchTerm,
        this.searchInputElement?.nativeElement,
        'Product Search'
      );
      this.navigateToPlp(searchTerm);
    } else {
      // Already on PLP, update the product list
      this.updateProductsList(searchTerm);
    }
  }

  onSearchButtonClick(event: MouseEvent): void {
    // Provide appropriate values for url, title, page, and position
    const url = AppConstants.routeUrls.plp;
    const title = this.searchInput.value || '';
    const page = 1;
    const position = 1;
    this.handleSearch(event, url, title, page, position);
  }

  handleSearch(
    event: MouseEvent,
    url: string,
    title: string,
    page: number,
    position: number
  ): void {
    const clickTarget = event.currentTarget as HTMLElement;

    // Track search result click
    this.searchBoxHeaderTrackingService.trackSearchResultClick(
      url,
      title,
      page,
      position,
      clickTarget
    );

    // Navigate to the clicked result
    this.router.navigate([url]);
  }

  onSearchClear(): void {
    this.searchInput.reset();
    this.searchedQuery = '';
  }

  ngOnDestroy(): void {
    this.searchBoxComponentService.clearResults();
    this.unsubscribe$.next(undefined);
    this.unsubscribe$.complete();
    this.globalService.clearMessagesOnDestroy();
  }
}
